import { useState, useEffect } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import {
  adminService,
  type AdminCourse,
  type Instructor,
  type Dimension,
} from "../services/adminService";
import Button from "../components/ui/Button/Button";
import InputField from "../components/ui/InputField/InputField";
import TextArea from "../components/ui/TextArea/TextArea";
import Dropdown from "../components/ui/Dropdown/Dropdown";
import Table from "../components/ui/Table/Table";
import SearchBar from "../components/ui/SearchBar/SearchBar";
import { AlertDialog } from "../components/ui/AlertDialog/AlertDialog";

export default function AdminCourses() {
  const [courses, setCourses] = useState<AdminCourse[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<AdminCourse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<AdminCourse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [coursesData, instructorsData, dimensionsData] = await Promise.all([
        adminService.getCourses(),
        adminService.getInstructors(),
        adminService.getDimensions(),
      ]);
      setCourses(coursesData);
      setInstructors(instructorsData);
      setDimensions(dimensionsData);
    } catch (err) {
      console.error("Failed to load data:", err);
      setError(err instanceof Error ? err.message : "Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const handleDeleteClick = (course: AdminCourse) => {
    setSelectedCourse(course);
    setDeleteError("");
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCourse) return;
    try {
      setDeleting(true);
      setDeleteError("");
      await adminService.deleteCourse(selectedCourse.id);
      setDeleteDialogOpen(false);
      setSelectedCourse(null);
      loadData();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete course");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedCourse(null);
    setDeleteError("");
  };

  const filteredCourses = courses.filter(
    (course) =>
      !searchTerm ||
      course.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.instructor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredCourses.length / ITEMS_PER_PAGE));
  const pagedCourses = filteredCourses.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  return (
    <div className="min-h-screen bg-[var(--color-grey-05)]">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 mb-6">
          <div>
            <h1 className="heading-3 mb-2">Course Management</h1>
            <p className="body text-[var(--color-grey-55)]">
              Manage all courses and assign instructors
            </p>
          </div>
          <Button variant="blue" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-5 h-5 mr-2" strokeWidth={2.5} />
            Create Course
          </Button>
        </div>

        {/* Search */}
        <div className="dashboard-card p-4">
          <SearchBar
            placeholder="Search by course code, title, or instructor..."
            onSearch={handleSearch}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Courses Table */}
        <div className="dashboard-card p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#181C62] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading courses...</p>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? "No courses match your search" : "No courses found"}
            </div>
          ) : (
            <>
            <Table
              noBorder
              data={[
                [
                  "Code",
                  "Title",
                  "Instructor",
                  "Term",
                  "Dimensions",
                  "Students",
                  "Submissions",
                  "Actions",
                ],
                ...pagedCourses.map((course) => [
                  course.code,
                  course.title,
                  course.instructor_name || "Unassigned",
                  course.term || "N/A",
                  <span
                    key={`dim-${course.id}`}
                    className="text-sm text-gray-600"
                  >
                    {course.dimension_ids?.length || 0} of 9
                  </span>,
                  course.enrollment_count,
                  course.submission_count,
                  <div className="flex gap-2" key={course.id}>
                    <Button
                      variant="blue"
                      onClick={() => setEditingCourse(course)}
                      className="p-1.5"
                      title="Edit course"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={2.5} />
                    </Button>
                    <Button
                      variant="red"
                      onClick={() => handleDeleteClick(course)}
                      className="p-1.5"
                      title="Delete course"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                    </Button>
                  </div>,
                ]),
              ]}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button
                  variant="grey"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="grey"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
            </>
          )}
        </div>

        {/* Create/Edit Course Modal */}
        {(showCreateModal || editingCourse) && (
          <CourseModal
            course={editingCourse}
            instructors={instructors}
            dimensions={dimensions}
            onClose={() => {
              setShowCreateModal(false);
              setEditingCourse(null);
            }}
            onSave={() => {
              loadData();
              setShowCreateModal(false);
              setEditingCourse(null);
            }}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {deleteDialogOpen && selectedCourse && (
          <AlertDialog
            isOpen={deleteDialogOpen}
            type={deleteError ? "error" : "warning"}
            title={deleteError ? "Deletion Failed" : "Delete Course"}
            message={
              deleteError
                ? `${deleteError}\n\nPlease try again.`
                : `Are you sure you want to delete "${selectedCourse.title}"? This action cannot be undone.`
            }
            primaryButtonText={
              deleteError ? "Try Again" : deleting ? "Deleting..." : "Delete"
            }
            secondaryButtonText="Cancel"
            onPrimaryAction={handleConfirmDelete}
            onSecondaryAction={handleCancelDelete}
            closeOnOverlayClick={false}
          />
        )}
      </div>
    </div>
  );
}

// Course Create/Edit Modal Component
function CourseModal({
  course,
  instructors,
  dimensions,
  onClose,
  onSave,
}: {
  course: AdminCourse | null;
  instructors: Instructor[];
  dimensions: Dimension[];
  onClose: () => void;
  onSave: () => void;
}) {
  const [formData, setFormData] = useState({
    code: course?.code || "",
    title: course?.title || "",
    description: course?.description || "",
    instructor_id: course?.instructor_id || "",
    term: course?.term || "",
    passcode: course?.passcode || "",
    dimension_ids: course?.dimension_ids || dimensions.map((d) => d.id),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Initialize dimension_ids with all dimensions if creating new course
  useEffect(() => {
    if (
      !course &&
      dimensions.length > 0 &&
      formData.dimension_ids.length === 0
    ) {
      setFormData((prev) => ({
        ...prev,
        dimension_ids: dimensions.map((d) => d.id),
      }));
    }
  }, [course, dimensions, formData.dimension_ids.length]);

  const instructorOptions = [
    { id: "", label: "No instructor assigned" },
    ...instructors.map((inst) => ({
      id: inst.id,
      label: `${inst.display_name} (${inst.email})`,
    })),
  ];

  const handleDimensionToggle = (dimensionId: number) => {
    setFormData((prev) => {
      const newDimensionIds = prev.dimension_ids.includes(dimensionId)
        ? prev.dimension_ids.filter((id) => id !== dimensionId)
        : [...prev.dimension_ids, dimensionId].sort((a, b) => a - b);
      return { ...prev, dimension_ids: newDimensionIds };
    });
  };

  const handleSelectAll = () => {
    setFormData((prev) => ({
      ...prev,
      dimension_ids: dimensions.map((d) => d.id),
    }));
  };

  const handleDeselectAll = () => {
    setFormData((prev) => ({
      ...prev,
      dimension_ids: [],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate at least one dimension is selected
    if (formData.dimension_ids.length === 0) {
      setError("Please select at least one dimension for this course");
      return;
    }

    setSaving(true);

    try {
      if (course) {
        // Update existing course
        await adminService.updateCourse(course.id, {
          code: formData.code,
          title: formData.title,
          description: formData.description,
          instructor_id: formData.instructor_id || undefined,
          term: formData.term,
          passcode: formData.passcode || undefined,
          dimension_ids: formData.dimension_ids,
        });
      } else {
        // Create new course
        await adminService.createCourse({
          code: formData.code,
          title: formData.title,
          description: formData.description,
          instructor_id: formData.instructor_id || undefined,
          term: formData.term,
          passcode: formData.passcode || undefined,
          dimension_ids: formData.dimension_ids,
        });
      }
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save course");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {course ? "Edit Course" : "Create New Course"}
        </h2>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded p-3 text-red-800 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <InputField
            label="Course Code"
            value={formData.code}
            onChange={(value: string) =>
              setFormData({ ...formData, code: value })
            }
            placeholder="e.g., CS101"
            required
          />

          <InputField
            label="Course Title"
            value={formData.title}
            onChange={(value: string) =>
              setFormData({ ...formData, title: value })
            }
            placeholder="e.g., Introduction to Computer Science"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <TextArea
              value={formData.description}
              onChange={(value: string) =>
                setFormData({ ...formData, description: value })
              }
              placeholder="Course description..."
              maxLength={1000}
              showCharCount
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Instructor
            </label>
            <Dropdown
              options={instructorOptions}
              selectedOption={
                instructorOptions.find(
                  (opt) => opt.id === formData.instructor_id
                ) || null
              }
              onSelect={(option) =>
                setFormData({ ...formData, instructor_id: option.id })
              }
            />
          </div>

          <InputField
            label="Term"
            value={formData.term}
            onChange={(value: string) =>
              setFormData({ ...formData, term: value })
            }
            placeholder="e.g., Fall 2025"
          />

          <InputField
            label="Course Passcode (Optional)"
            value={formData.passcode}
            onChange={(value: string) =>
              setFormData({ ...formData, passcode: value })
            }
            placeholder="Leave blank to use course code as passcode"
          />
          <p className="text-xs text-gray-500 -mt-2">
            Case-sensitive. Students must enter this passcode to enroll.
          </p>

          {/* Dimension Selection */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Available Dimensions ({formData.dimension_ids.length} of{" "}
                {dimensions.length} selected)
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear All
                </button>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 max-h-60 overflow-y-auto bg-gray-50">
              {dimensions.map((dim) => (
                <label
                  key={dim.id}
                  className="flex items-start gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData.dimension_ids.includes(dim.id)}
                    onChange={() => handleDimensionToggle(dim.id)}
                    className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900">
                      {dim.id}. {dim.label}
                    </span>
                    {dim.short_label && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({dim.short_label})
                      </span>
                    )}
                  </div>
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: `${dim.color_hex}20`,
                      color: dim.color_hex,
                    }}
                  >
                    ●
                  </span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Choose the ILA dimensions to be utilized for scoring in this
              course.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="blue"
              disabled={saving}
              className="flex-1"
            >
              {saving
                ? "Saving..."
                : course
                ? "Update Course"
                : "Create Course"}
            </Button>
            <Button
              type="button"
              variant="grey"
              onClick={onClose}
              disabled={saving}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
